// 智能抠图
import { ModuleEnum } from "../moduleEnum";
import type { LayoutReferState } from "@/types/image";
import { useEventTracking } from "@/hooks/useEventTracking";
import { useGeneratingVersions } from "@/hooks/useGeneratingVersions";
import { getClientId } from "@/hooks/useGenerate";
import { initGenerateResult } from "@/hooks/useGenerate";
import type { ModuleRoute, Module } from "@/types/artwork/generate";
import { request } from "@/axios";
import { Poller, defaultCheckCompletion } from "@/utils/QuickTaskPoller";
import { $bus } from "@/mitt";
import { useProgress } from "@/hooks/useProgress";

export const module = ModuleEnum["matting"];

// 生图参数
export const mattingParams = {
  module,
  layoutRefer: null as null | LayoutReferState // 原始稿件
};

import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { filteringInvalidFields } from "@/utils";
import { getModuleLabelById, getConsumeTime } from "@xmiles/holopix-helper";

// 将当前用户输入的生图参数转换成后端所需要
function getMattingGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  let params = {
    module,
    source: 1,
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    layoutRefer: artworkStore.matting.params.layoutRefer,
    layoutReferId: artworkStore.matting.params.layoutRefer?.id,
    originImgUrl: artworkStore.matting.params.layoutRefer?.image,
    batchSize: 1
  } as any;

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  return params;
}

// 查询生图状态
function queryGenerateStatus(data: { module: Module; apiClientId: string; userId: string; artworkId: number; originImgUrl: string }) {
  return request({
    api: "/aiApi/remBg",
    method: "post",
    data: { message: JSON.stringify(data) },
    notToast: false
  });
}

// 定义响应接口
interface MattingResponse {
  msg: string;
  code: number;
  data: {
    imgUrl: string | null;
    imgId: string | null;
    status: number;
    requestId: string;
    msg: string | null;
  };
  success: boolean;
}

const { start, close, onProgressUpdated } = useProgress();

// 开启模块生图
export async function startMattingGenerate(oldParams?: any) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const params = oldParams ?? getMattingGenerateParam();

  const clientId = oldParams?.apiClientId || getClientId();
  params.apiClientId = clientId;

  const poller = new Poller<MattingResponse>({
    interval: 1500,
    shouldLog: true,
    logMessage: `耗时计算-一键抠图`
  });

  const { set, updateResult, isModuleLatest, del } = useGeneratingVersions();

  poller
    .on("complete", async (result: any) => {
      // 通知历史记录侧边栏有新的版本生成
      $bus.emit("hasNewVersion");

      // 该模块最新一次生图
      if (isModuleLatest(clientId)) {
        artworkStore.resetModuleLastVersionParams(module, params);

        const moduleRouteName = ModuleEnum[module] as ModuleRoute;

        // 更新生图面板状态
        artworkStore[moduleRouteName].result = {
          ...artworkStore[moduleRouteName].result,
          code: 3,
          data: {
            status: 3,
            images: [
              {
                id: result.data.imgId,
                width: result.data.width,
                height: result.data.height,
                image: result.data.imgUrl
              }
            ],
            param: JSON.stringify(params)
          }
        };

        // 移除该条生图记录
        del(clientId);

        close();
      }

      reportFinishEventTracking({
        result: 1,
        imageIds: result.data.imgUrl
      });
    })
    .on("error", (error: any) => {
      console.error("一键抠图错误:", error);

      del(clientId);
      artworkStore.setModuleLatest(module);

      // 关闭虚拟进度条
      close();

      // 生图失败、更新用户算力
      userStore.queryUserRights();

      reportFinishEventTracking({
        result: 0
      });
    })
    .poll(() => queryGenerateStatus(params), defaultCheckCompletion);

  close();

  const estimatedTime = getConsumeTime(module, params);
  start(estimatedTime);

  // 更新生图算力
  setTimeout(() => {
    userStore.queryUserRights();
  }, 1500);

  onProgressUpdated((progress) => {
    updateResult(clientId, { progress });
  });

  // 添加生图记录
  set({
    module,
    clientId,
    params,
    result: { ...initGenerateResult, code: 2, progress: 1, msg: "正在快速生成中" },
    socket: undefined,
    cancel: poller.cancel
  });

  // 更新生图面板状态
  const moduleRouteName = ModuleEnum[module] as ModuleRoute;
  artworkStore[moduleRouteName].result = {
    ...artworkStore[moduleRouteName].result,
    code: 2
  };

  // 提交生图埋点
  function reportStartEventTracking() {
    const eventValue: any = {
      client_id: clientId,
      source: "直接生成",
      module: getModuleLabelById(module) as string,
      artwork_id: artworkStore.artworkState.id.toString(),
      batchSize: params.batchSize,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0
    };

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }
  reportStartEventTracking();

  function reportFinishEventTracking({ result = 0 as 0 | 1, imageIds = "", queue_time_number = 0, loading_time_number = 0 } = {}) {
    useEventTracking({
      event: "artwork_status_event",
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id: clientId,
        source: "直接生成",
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0
      }
    });
  }
}
